import {
  encodePolyline,
  formatDistanceMeters,
  formatDurationSeconds,
  formatRideDate,
  formatSpeedMps,
  METRICS,
  METRIC_ORDER,
  type RideTrackPoint,
  type RouteDetail,
} from '@motogo/shared';
import { ArrowLeft, BookmarkPlus, Pause, Play } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import RideGGChart from './RideGGChart';
import RideTraceChart, { type ChartView } from './RideTraceChart';

import { useAuth } from '@/hooks/useAuth';
import { useRideDetail } from '@/hooks/useRideDetail';
import { useRideReview } from '@/hooks/useRideReview';
import { putLocalRoute } from '@/lib/routeStore';
import { saveRoute } from '@/lib/savedRoutes';

// Replay cadence — one stored point per tick. Tracks are downsampled (~1 pt / 2 s), so this
// plays a ride back at roughly 100–200× depending on density. Good enough for a first pass.
const REPLAY_MS = 110;

const EMPTY_TRACK: RideTrackPoint[] = [];

// Ride review: the track + speed-gradient line live on the persistent map (driven via
// RideReview context); this page is the floating header + the stats/chart panel over it.
// Speed always; Long-G / Lean traces + the scrub-driven G-G appear when the ride has
// telemetry. Charts share one time cursor (scrubIndex) with the map. Metric-selector +
// map-recolour-by-metric is a follow-up.
type SaveRouteStatus = 'closed' | 'editing' | 'saving' | 'saved' | 'queued' | 'signedout';

const RideDetailPage: FC = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const state = useRideDetail(id);
  const { state: authState } = useAuth();
  const { scrubIndex, metric, setReview, setScrubIndex, setMetric } = useRideReview();

  const [playing, setPlaying] = useState(false);

  // "Save as route" — converts this ride's track into a reusable saved route. The server
  // simplifies plan-less geometry, so the raw track goes up as-is.
  const [routeSave, setRouteSave] = useState<SaveRouteStatus>('closed');
  const [routeName, setRouteName] = useState('');
  const [routeSaveError, setRouteSaveError] = useState<string | null>(null);

  const track = state.status === 'ready' ? state.track : EMPTY_TRACK;
  const trackLength = track.length;

  const times = useMemo(() => track.map((p) => Date.parse(p.timestamp)), [track]);
  const speedVals = useMemo(() => track.map(METRICS.speed.value), [track]);
  const longGVals = useMemo(() => track.map(METRICS.longg.value), [track]);
  const leanVals = useMemo(() => track.map(METRICS.lean.value), [track]);
  const ggLong = useMemo(() => track.map((p) => p.ggLongG ?? null), [track]);
  const ggLat = useMemo(() => track.map((p) => p.ggLatG ?? null), [track]);
  const hasTelemetry = useMemo(
    () => track.some((p) => p.longG != null || p.leanDeg != null || p.ggLongG != null),
    [track],
  );

  // Per-metric peak moments — value + the track index, so each stat can jump the cursor there.
  const peaks = useMemo(() => {
    let speedIdx = -1;
    let speedVal = -Infinity;
    let leanIdx = -1;
    let leanVal = -Infinity; // by magnitude
    let accelIdx = -1;
    let accelVal = -Infinity; // most positive longG
    let decelIdx = -1;
    let decelVal = Infinity; // most negative longG
    track.forEach((p, i) => {
      if (p.speedMps != null && p.speedMps > speedVal) {
        speedVal = p.speedMps;
        speedIdx = i;
      }
      if (p.leanDeg != null && Math.abs(p.leanDeg) > leanVal) {
        leanVal = Math.abs(p.leanDeg);
        leanIdx = i;
      }
      if (p.longG != null && p.longG > accelVal) {
        accelVal = p.longG;
        accelIdx = i;
      }
      if (p.longG != null && p.longG < decelVal) {
        decelVal = p.longG;
        decelIdx = i;
      }
    });
    return { speedIdx, leanIdx, accelIdx, accelVal, decelIdx, decelVal };
  }, [track]);

  // Shared zoom window across all stacked traces + the G-G cloud. null = full ride.
  const [view, setView] = useState<ChartView | null>(null);
  useEffect(() => setView(null), [id]); // reset zoom when switching rides

  const fullT0 = trackLength > 0 ? times[0] : 0;
  const fullT1 = trackLength > 1 ? times[trackLength - 1] : fullT0 + 1;
  const effView: ChartView = view ?? { t0: fullT0, t1: fullT1 };
  const zoomed = view != null && (view.t0 > fullT0 + 1 || view.t1 < fullT1 - 1);

  const handleViewChange = useCallback((t0: number, t1: number) => setView({ t0, t1 }), []);
  const resetView = useCallback(() => setView(null), []);

  // Index range the G-G cloud should show, mirroring the visible time window (±1 for continuity).
  const ggBounds = useMemo(() => {
    if (!view || trackLength === 0) return { lo: 0, hi: trackLength - 1 };
    let lo = 0;
    while (lo < trackLength && times[lo] < view.t0) lo++;
    let hi = trackLength - 1;
    while (hi > 0 && times[hi] > view.t1) hi--;
    return { lo: Math.max(0, lo - 1), hi: Math.min(trackLength - 1, hi + 1) };
  }, [view, times, trackLength]);

  // Push the track onto the map while this page is mounted; clear it on leave.
  useEffect(() => {
    if (state.status === 'ready') setReview(state.track);
    return () => setReview(null);
  }, [state, setReview]);

  // Mirror the cursor into a ref so the replay interval can advance from wherever it is
  // (including after a manual scrub) without re-subscribing each tick.
  const scrubRef = useRef<number | null>(scrubIndex);
  scrubRef.current = scrubIndex;

  useEffect(() => {
    if (!playing || trackLength < 2) return;
    const timer = setInterval(() => {
      const next = (scrubRef.current ?? -1) + 1;
      if (next >= trackLength) {
        setScrubIndex(trackLength - 1);
        setPlaying(false);
      } else {
        setScrubIndex(next);
      }
    }, REPLAY_MS);
    return () => clearInterval(timer);
  }, [playing, trackLength, setScrubIndex]);

  function togglePlay() {
    if (trackLength < 2) return;
    if (!playing && (scrubIndex == null || scrubIndex >= trackLength - 1)) {
      setScrubIndex(0); // restart from the top when at (or past) the end
    }
    setPlaying((p) => !p);
  }

  function handleScrub(i: number) {
    setPlaying(false); // manual scrub takes over from replay
    setScrubIndex(i);
  }

  const title =
    state.status === 'ready' ? (state.summary.name ?? formatRideDate(state.summary.startedAt)) : 'Ride';

  function openRouteSave() {
    if (state.status !== 'ready') return;
    if (authState.status === 'guest') {
      setRouteSave('signedout'); // routes are account-bound, unlike guest-local rides
      return;
    }
    setRouteName(title);
    setRouteSaveError(null);
    setRouteSave('editing');
  }

  async function handleRouteSave() {
    if (state.status !== 'ready' || routeSave === 'saving') return;
    const name = routeName.trim();
    if (!name) return;

    const coordinates = state.track
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
      .map((p) => [p.lon, p.lat] as [number, number]);
    if (coordinates.length < 2) return;

    const routeId = crypto.randomUUID();
    setRouteSave('saving');
    setRouteSaveError(null);
    try {
      const saved = await saveRoute({
        id: routeId,
        name,
        source: 'ride',
        coordinates,
        durationSeconds: state.summary.durationSeconds,
        plan: null,
      });
      if (saved) {
        await putLocalRoute(saved, true).catch(() => undefined);
        setRouteSave('saved');
      } else {
        setRouteSave('signedout');
      }
    } catch (e) {
      // Offline — queue in the mirror; the library's sync pass retries idempotently.
      const now = new Date().toISOString();
      const encoded = encodePolyline(coordinates.map(([lon, lat]) => [lat, lon] as [number, number]));
      const local: RouteDetail = {
        id: routeId,
        name,
        description: null,
        source: 'ride',
        visibility: 'private',
        distanceMeters: state.summary.distanceMeters,
        durationSeconds: state.summary.durationSeconds,
        overviewPolyline: state.summary.overviewPolyline || encoded,
        polyline: encoded,
        plan: null,
        createdAt: now,
        updatedAt: now,
        ownerName: null,
      };
      try {
        await putLocalRoute(local, false);
        setRouteSave('queued');
      } catch {
        setRouteSave('editing');
        setRouteSaveError(e instanceof Error ? e.message : 'Saving failed');
      }
    }
  }

  return (
    <div className="mg-review">
      <header className="mg-review-header">
        <button className="mg-icon-btn" onClick={() => navigate('/rides')} aria-label="Back to rides" title="Back to rides">
          <ArrowLeft size={26} />
        </button>
        <h1 className="mg-review-title">{title}</h1>
        {state.status === 'ready' && state.track.length >= 2 && routeSave === 'closed' && (
          <button
            className="mg-icon-btn"
            onClick={openRouteSave}
            aria-label="Save as route"
            title="Save as route"
          >
            <BookmarkPlus size={24} />
          </button>
        )}
      </header>

      <section className="mg-review-panel">
        {state.status === 'loading' && <p className="mg-empty">Loading ride…</p>}
        {state.status === 'notfound' && <p className="mg-empty">Ride not found</p>}
        {state.status === 'error' && (
          <>
            <p className="mg-empty">Couldn&rsquo;t load ride</p>
            <p className="mg-empty-sub">{state.error}</p>
          </>
        )}

        {state.status === 'ready' && (
          <>
            {routeSave !== 'closed' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                {routeSave === 'saved' || routeSave === 'queued' ? (
                  <p className="mg-review-note" style={{ margin: 0 }}>
                    {routeSave === 'saved' ? 'Saved as route' : 'Saved on this device — syncs when online'}{' '}
                    <button className="mg-link-btn" onClick={() => navigate('/routes')}>
                      View
                    </button>
                  </p>
                ) : routeSave === 'signedout' ? (
                  <p className="mg-review-note" style={{ margin: 0 }}>Sign in to save this ride as a route.</p>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        className="mg-input"
                        style={{ flex: 1 }}
                        value={routeName}
                        maxLength={120}
                        placeholder="Route name"
                        onChange={(e) => setRouteName(e.target.value)}
                      />
                      <button
                        className="mg-btn mg-btn-primary"
                        style={{ width: 'auto', padding: '0 18px' }}
                        disabled={routeSave === 'saving' || !routeName.trim()}
                        onClick={() => void handleRouteSave()}
                      >
                        {routeSave === 'saving' ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                    {routeSaveError && <p className="mg-review-note" style={{ margin: 0 }}>{routeSaveError}</p>}
                  </>
                )}
              </div>
            )}

            <div className="mg-review-stats">
              <Stat value={formatDistanceMeters(state.summary.distanceMeters)} label="Distance" />
              <Stat value={formatDurationSeconds(state.summary.durationSeconds)} label="Duration" />
              <Stat value={formatSpeedMps(state.summary.avgSpeedMps)} label="Avg" />
              <Stat
                value={formatSpeedMps(state.summary.maxSpeedMps)}
                label="Max"
                onJump={peaks.speedIdx >= 0 ? () => handleScrub(peaks.speedIdx) : undefined}
              />
              {state.summary.maxLeanDeg != null && (
                <Stat
                  value={`${Math.round(state.summary.maxLeanDeg)}°`}
                  label="Max lean"
                  onJump={peaks.leanIdx >= 0 ? () => handleScrub(peaks.leanIdx) : undefined}
                />
              )}
              {peaks.accelIdx >= 0 && peaks.accelVal > 0 && (
                <Stat
                  value={`+${peaks.accelVal.toFixed(2)} g`}
                  label="Max accel"
                  onJump={() => handleScrub(peaks.accelIdx)}
                />
              )}
              {peaks.decelIdx >= 0 && peaks.decelVal < 0 && (
                <Stat
                  value={`${peaks.decelVal.toFixed(2)} g`}
                  label="Max decel"
                  onJump={() => handleScrub(peaks.decelIdx)}
                />
              )}
            </div>

            {hasTelemetry && (
              <div className="mg-metric-tabs" role="group" aria-label="Colour the map by">
                {METRIC_ORDER.map((m) => (
                  <button
                    key={m}
                    className={m === metric ? 'mg-metric-tab active' : 'mg-metric-tab'}
                    onClick={() => setMetric(m)}
                  >
                    {METRICS[m].label}
                  </button>
                ))}
              </div>
            )}

            <div className={metric === 'speed' ? 'mg-chart-block active' : 'mg-chart-block'}>
              <RideTraceChart
                times={times}
                values={speedVals}
                scrubIndex={scrubIndex}
                onScrub={handleScrub}
                metric="speed"
                view={effView}
                onViewChange={handleViewChange}
                onResetView={resetView}
              />
            </div>

            {hasTelemetry && (
              <>
                <div className={metric === 'longg' ? 'mg-chart-block active' : 'mg-chart-block'}>
                  <RideTraceChart
                    times={times}
                    values={longGVals}
                    scrubIndex={scrubIndex}
                    onScrub={handleScrub}
                    metric="longg"
                    view={effView}
                    onViewChange={handleViewChange}
                    onResetView={resetView}
                  />
                </div>
                <div className={metric === 'lean' ? 'mg-chart-block active' : 'mg-chart-block'}>
                  <RideTraceChart
                    times={times}
                    values={leanVals}
                    scrubIndex={scrubIndex}
                    onScrub={handleScrub}
                    metric="lean"
                    view={effView}
                    onViewChange={handleViewChange}
                    onResetView={resetView}
                  />
                </div>
              </>
            )}

            <div className="mg-review-controls">
              <button className="mg-icon-btn" onClick={togglePlay} aria-label={playing ? 'Pause replay' : 'Play replay'}>
                {playing ? <Pause size={24} /> : <Play size={24} />}
              </button>
              <span className="mg-review-hint">
                {scrubIndex != null
                  ? `+${formatElapsed(track, scrubIndex)}`
                  : 'Drag a chart to scrub · ▶ to replay'}
              </span>
              {zoomed && (
                <button className="mg-zoom-reset" onClick={resetView}>
                  Reset zoom
                </button>
              )}
            </div>

            {hasTelemetry ? (
              <div className="mg-gg-row">
                <RideGGChart
                  ggLongG={ggLong}
                  ggLatG={ggLat}
                  scrubIndex={scrubIndex}
                  cloudLo={ggBounds.lo}
                  cloudHi={ggBounds.hi}
                />
                <span className="mg-review-note">Friction-circle usage — the cursor marks the moment.</span>
              </div>
            ) : (
              <p className="mg-review-note">Lean &amp; G-force charts appear once a ride is recorded with telemetry.</p>
            )}
          </>
        )}
      </section>
    </div>
  );
};

const Stat: FC<{ value: string; label: string; onJump?: () => void }> = ({ value, label, onJump }) =>
  onJump ? (
    <button type="button" className="mg-stat mg-stat-tappable" onClick={onJump} title="Jump to this moment">
      <span className="mg-stat-value">{value}</span>
      <span className="mg-stat-label">{label}</span>
    </button>
  ) : (
    <div className="mg-stat">
      <span className="mg-stat-value">{value}</span>
      <span className="mg-stat-label">{label}</span>
    </div>
  );

function formatElapsed(track: { timestamp: string }[], index: number): string {
  const seconds = Math.max(0, (Date.parse(track[index].timestamp) - Date.parse(track[0].timestamp)) / 1000);
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export default RideDetailPage;
